import { Component } from '@theme/component';

const ACTIVE_CLASS = 'blog-post-card--active';
const ITEM_ACTIVE_CLASS = 'blog-post-item--active';

/**
 * Dstrezzed blog post card: hover/tap adds `--active`, a second tap navigates.
 *
 * @extends {Component}
 */
class BlogPostCard extends Component {
  /** @type {boolean} */
  #isDstrezzed = false;

  connectedCallback() {
    super.connectedCallback();

    this.#isDstrezzed = this.classList.contains('blog-post-card--dstrezzed');
    if (!this.#isDstrezzed) return;

    document.addEventListener('click', this.#handleDocumentClick);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
  }

  /**
   * First tap/click reveals the overlay. A second click follows the article link.
   * @param {Event} event
   */
  handleActivate(event) {
    if (!this.classList.contains('blog-post-card--dstrezzed') || Shopify.designMode) return;
    if (this.#isActive()) return;

    event.preventDefault();
    this.#setActive(true);
  }

  /**
   * Desktop hover reveals the overlay via `--active`.
   * @param {Event} event
   */
  handlePointerEnter(event) {
    if (!this.#isHoverPointer(event) || Shopify.designMode) return;

    this.#setActive(true);
  }

  /**
   * @param {Event} event
   */
  handlePointerLeave(event) {
    if (!this.#isHoverPointer(event) || Shopify.designMode) return;

    this.#setActive(false);
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isHoverPointer(event) {
    return event instanceof PointerEvent && event.pointerType === 'mouse';
  }

  #handleFocusIn = () => {
    if (Shopify.designMode) return;

    this.#setActive(true);
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (Shopify.designMode) return;

    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;

    this.#setActive(false);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (Shopify.designMode || !this.#isActive()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.contains(target)) return;

    this.#setActive(false);
  };

  #isActive() {
    return this.classList.contains(ACTIVE_CLASS);
  }

  /**
   * @param {boolean} isActive
   */
  #setActive(isActive) {
    if (isActive) {
      this.#deactivateOthers();
    }

    this.classList.toggle(ACTIVE_CLASS, isActive);
    this.closest('.blog-post-item')?.classList.toggle(ITEM_ACTIVE_CLASS, isActive);
  }

  #deactivateOthers() {
    const cards = document.querySelectorAll(`blog-post-card.${ACTIVE_CLASS}`);

    for (const card of cards) {
      if (card === this) continue;

      card.classList.remove(ACTIVE_CLASS);
      card.closest('.blog-post-item')?.classList.remove(ITEM_ACTIVE_CLASS);
    }
  }
}

if (!customElements.get('blog-post-card')) {
  customElements.define('blog-post-card', BlogPostCard);
}
